import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Deposit.css';

interface Cryptocurrency {
  id: string;
  name: string;
  fullName: string;
  icon: string;
  color: string;
  network?: string;
}

const mockCryptos: Cryptocurrency[] = [
  { id: 'usdt', name: 'USDT', fullName: 'Tether', icon: '₮', color: '#26a17b' },
  { id: 'btc', name: 'BTC', fullName: 'Bitcoin', icon: '₿', color: '#f7931a' },
  { id: 'eth', name: 'ETH', fullName: 'Ethereum', icon: 'Ξ', color: '#627eea' },
  { id: 'bnb', name: 'BNB', fullName: 'BNB Chain', icon: '◆', color: '#f3ba2f' },
  { id: 'sol', name: 'SOL', fullName: 'Solana', icon: '◎', color: '#9945ff' },
  { id: 'xrp', name: 'XRP', fullName: 'Ripple', icon: '✕', color: '#00aae4' },
  { id: 'ada', name: 'ADA', fullName: 'Cardano', icon: '₳', color: '#0033ad' },
  { id: 'doge', name: 'DOGE', fullName: 'Dogecoin', icon: 'Ð', color: '#c2a633' },
  { id: 'dot', name: 'DOT', fullName: 'Polkadot', icon: '●', color: '#e6007a' },
  { id: 'matic', name: 'MATIC', fullName: 'Polygon', icon: '⬡', color: '#8247e5' },
  { id: 'link', name: 'LINK', fullName: 'Chainlink', icon: '⬡', color: '#2a5ada' },
  { id: 'avax', name: 'AVAX', fullName: 'Avalanche', icon: 'A', color: '#e84142' },
  { id: 'atom', name: 'ATOM', fullName: 'Cosmos', icon: '⚛', color: '#2e3148' },
  { id: 'ltc', name: 'LTC', fullName: 'Litecoin', icon: 'Ł', color: '#bfbbbb' },
  { id: 'uni', name: 'UNI', fullName: 'Uniswap', icon: '🦄', color: '#ff007a' },
  { id: 'trx', name: 'TRX', fullName: 'TRON', icon: '◈', color: '#ff0013' },
  { id: 'near', name: 'NEAR', fullName: 'NEAR Protocol', icon: 'Ⓝ', color: '#00ec97' },
  { id: 'ftm', name: 'FTM', fullName: 'Fantom', icon: '◇', color: '#1969ff' },
  { id: 'algo', name: 'ALGO', fullName: 'Algorand', icon: 'Ⱥ', color: '#000000' },
  { id: 'xlm', name: 'XLM', fullName: 'Stellar', icon: '✦', color: '#000000' },
  { id: 'aave', name: 'AAVE', fullName: 'Aave', icon: '👻', color: '#b6509e' },
  { id: 'sand', name: 'SAND', fullName: 'The Sandbox', icon: 'S', color: '#00adef' },
  { id: 'mana', name: 'MANA', fullName: 'Decentraland', icon: 'Ɱ', color: '#ff2d55' },
  { id: 'axs', name: 'AXS', fullName: 'Axie Infinity', icon: '∞', color: '#0055d5' },
];

const ITEMS_PER_PAGE = 6;

const mockNetworks = [
  { id: 'ton', name: 'TON' },
  { id: 'polygon', name: 'POLYGON' },
  { id: 'arbitrum', name: 'ARBITRUM' },
  { id: 'sol', name: 'SOL' },
  { id: 'near', name: 'NEAR' },
  { id: 'trc20', name: 'TRC20' }
];

const Deposit: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCrypto, setSelectedCrypto] = useState(mockCryptos[0]);
  const [selectedNetwork, setSelectedNetwork] = useState(mockNetworks[5]);
  const [copied, setCopied] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const walletAddress = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb';

  // Filter cryptocurrencies based on search query
  const filteredCryptos = useMemo(() => {
    if (!searchQuery.trim()) return mockCryptos;
    const query = searchQuery.toLowerCase();
    return mockCryptos.filter(
      crypto =>
        crypto.name.toLowerCase().includes(query) ||
        crypto.fullName.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCryptos.length / ITEMS_PER_PAGE);
  const paginatedCryptos = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCryptos.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCryptos, currentPage]);

  // Reset page when search changes
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  const handleSelectCrypto = useCallback((crypto: Cryptocurrency) => {
    setSelectedCrypto(crypto);
    handleCloseModal();
  }, [handleCloseModal]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewHistory = () => {
    console.log('Navigate to deposit history');
  };

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="deposit-container">
      <header className="deposit-header">
        <button className="deposit-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="deposit-title">Deposit Crypto</h1>
      </header>

      <div className="deposit-content">
        <div className="deposit-section">
          <label className="deposit-label">Select Cryptocurrency</label>
          <button className="deposit-crypto-selector" onClick={handleOpenModal}>
            <div className="deposit-crypto-info">
              <div className="deposit-crypto-icon" style={{ backgroundColor: selectedCrypto.color }}>
                {selectedCrypto.icon}
              </div>
              <div className="deposit-crypto-details">
                <span className="deposit-crypto-name">{selectedCrypto.name}</span>
                <span className="deposit-crypto-fullname">{selectedCrypto.fullName}</span>
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="deposit-section">
          <label className="deposit-label">Select Network</label>
          <div className="deposit-network-grid">
            {mockNetworks.map((network) => (
              <button
                key={network.id}
                className={`deposit-network-button ${selectedNetwork.id === network.id ? 'active' : ''}`}
                onClick={() => setSelectedNetwork(network)}
              >
                {network.name}
              </button>
            ))}
          </div>
        </div>

        <div className="deposit-qr-section">
          <div className="deposit-qr-container">
            <div className="deposit-qr-placeholder">
              <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
                <rect x="8" y="8" width="32" height="32" fill="currentColor"/>
                <rect x="56" y="8" width="32" height="32" fill="currentColor"/>
                <rect x="8" y="56" width="32" height="32" fill="currentColor"/>
                <rect x="56" y="56" width="32" height="32" fill="currentColor"/>
                <rect x="16" y="16" width="16" height="16" fill="white"/>
                <rect x="64" y="16" width="16" height="16" fill="white"/>
                <rect x="16" y="64" width="16" height="16" fill="white"/>
                <rect x="64" y="64" width="16" height="16" fill="white"/>
              </svg>
            </div>
          </div>
          <p className="deposit-qr-instruction">Scan QR code to deposit</p>
        </div>

        <div className="deposit-section">
          <label className="deposit-label">Deposit Address</label>
          <div className="deposit-address-container">
            <div className="deposit-address-text">{walletAddress}</div>
            <button 
              className="deposit-copy-button" 
              onClick={handleCopyAddress}
              aria-label="Copy address"
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="5.33333" y="5.33333" width="9.33333" height="9.33333" rx="1.33333" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10.6667 5.33333V2.66667C10.6667 2.29848 10.3682 2 10 2H2.66667C2.29848 2 2 2.29848 2 2.66667V10C2 10.3682 2.29848 10.6667 2.66667 10.6667H5.33333" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="deposit-notice">
          <div className="deposit-notice-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="10" cy="13" r="0.5" fill="currentColor"/>
            </svg>
            <h3 className="deposit-notice-title">⚠️ Important Notice</h3>
          </div>
          <ul className="deposit-notice-list">
            <li>• Only send {selectedCrypto.name} to this address</li>
            <li>• Ensure you select the correct network ({selectedNetwork.name})</li>
            <li>• Minimum deposit: 0.001 {selectedCrypto.name}</li>
            <li>• Deposits arrive after 12 network confirmations</li>
          </ul>
        </div>

        <button className="deposit-history-button" onClick={handleViewHistory}>
          View Deposit History
        </button>
      </div>

      {/* Cryptocurrency Selection Modal */}
      {isModalOpen && (
        <div className="crypto-modal-overlay" onClick={handleCloseModal}>
          <div className="crypto-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="crypto-modal-header">
              <h2 className="crypto-modal-title">Select Cryptocurrency</h2>
              <button className="crypto-modal-close" onClick={handleCloseModal} aria-label="Close modal">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="crypto-modal-search">
              <div className="crypto-search-input-wrapper">
                <svg className="crypto-search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="crypto-search-input"
                  placeholder="Search by name or symbol..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    className="crypto-search-clear"
                    onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                    aria-label="Clear search"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" fill="currentColor" fillOpacity="0.2"/>
                      <path d="M10.5 5.5L5.5 10.5M5.5 5.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Results count */}
            <div className="crypto-modal-results-info">
              <span className="crypto-results-count">
                {filteredCryptos.length} cryptocurrenc{filteredCryptos.length !== 1 ? 'ies' : 'y'} found
              </span>
            </div>

            {/* Cryptocurrency List */}
            <div className="crypto-modal-list">
              {paginatedCryptos.length > 0 ? (
                paginatedCryptos.map((crypto) => (
                  <button
                    key={crypto.id}
                    className={`crypto-modal-item ${selectedCrypto.id === crypto.id ? 'selected' : ''}`}
                    onClick={() => handleSelectCrypto(crypto)}
                  >
                    <div className="crypto-item-icon" style={{ backgroundColor: crypto.color }}>
                      {crypto.icon}
                    </div>
                    <div className="crypto-item-info">
                      <span className="crypto-item-name">{crypto.name}</span>
                      <span className="crypto-item-fullname">{crypto.fullName}</span>
                    </div>
                    {selectedCrypto.id === crypto.id && (
                      <div className="crypto-item-selected">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="7" fill="#22c35d"/>
                          <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="crypto-modal-empty">
                  <div className="crypto-empty-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                      <path d="M18 24H30M24 18V30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.3"/>
                    </svg>
                  </div>
                  <p className="crypto-empty-text">No cryptocurrencies found</p>
                  <p className="crypto-empty-subtext">Try adjusting your search</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="crypto-modal-pagination">
                <button
                  className="crypto-pagination-btn crypto-pagination-arrow"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                <div className="crypto-pagination-numbers">
                  {getPageNumbers().map((page, index) => (
                    typeof page === 'number' ? (
                      <button
                        key={index}
                        className={`crypto-pagination-btn crypto-pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ) : (
                      <span key={index} className="crypto-pagination-ellipsis">{page}</span>
                    )
                  ))}
                </div>

                <button
                  className="crypto-pagination-btn crypto-pagination-arrow"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposit;
